/**
 * Online routing wire client — POST {baseURL}/api/v2/routes, the London city
 * server (TASK-986; `/api/v1` stays on the backend for old app versions).
 *
 * 1:1 port of `metro-ios/MetroCore/Sources/Routing/OnlineRoutingClient.swift`.
 * The backend (`metro-server/services/routing`) is the same service the iOS
 * + Android clients call, so the wire-format must stay byte-identical across
 * all three platforms — see `docs/superpowers/specs/TASK-273-web-port-spec.md`
 * for the canonical contract.
 *
 * The client is intentionally thin: it owns the transport (`fetch` + timeout
 * + error mapping), the JSON decoder (`{ data }` envelope → `Route`), and
 * signature dedup. Whether the returned ids exist on the map is the host's
 * schema guard's call (`buildOnlineRouteSchemaGuard`). All
 * orchestration — cache, online ↔ offline merge, mode-class Pareto — lives
 * one layer up in `routingAdapter`.
 */
import type { LegType, Route, Stop } from './route'
import { recomputeID, signatureKey } from './route'

// MARK: — Options + error taxonomy

/**
 * Accessibility preference flavours TFL Journey Planner accepts. Encoded as
 * the literal strings the backend expects; the wire format omits the field
 * entirely when `undefined` (matches iOS behaviour of dropping empty
 * options — see `_RequestOptions` encoding in
 * `metro-ios/.../OnlineRoutingClient.swift:295–312`).
 */
export type AccessibilityPreference =
  | 'noRequirements'
  | 'noStairs'
  | 'noEscalators'
  | 'stepFreeToVehicle'
  | 'stepFreeToPlatform'

/**
 * Per-call options. All three fields are optional; the JSON encoder drops
 * any field whose value is `undefined`. `depart_at` and `arrive_at` are
 * mutually exclusive — when both are undefined the backend defaults to
 * depart-now.
 *
 * The `*_at` values are ISO-8601 strings in London local time (no timezone
 * suffix). Caller is responsible for formatting.
 */
export interface OnlineRoutingOptions {
  readonly departAt?: string
  readonly arriveAt?: string
  readonly accessibilityPreference?: AccessibilityPreference
}

export type OnlineRoutingErrorKind =
  | { kind: 'timeout' }
  | { kind: 'transport'; cause: unknown }
  | { kind: 'upstream'; status: number; body?: string }
  | { kind: 'decode'; cause: unknown }

export class OnlineRoutingError extends Error {
  readonly detail: OnlineRoutingErrorKind
  constructor(detail: OnlineRoutingErrorKind) {
    super(detail.kind)
    this.detail = detail
    this.name = 'OnlineRoutingError'
  }
}

// MARK: — Client interface + defaults

export interface OnlineRoutingClient {
  /**
   * POST `/api/v2/routes` with the given NaPTAN endpoints and options.
   *
   * Returns the decoded route list (post signature dedup). Empty array is a legitimate response (TFL Journey
   * Planner can return zero journeys for valid pairs) — caller decides
   * whether to fall back to offline.
   *
   * Pass `signal` to abort the in-flight request when the caller no
   * longer cares about the result (rapid From/To churn, dismissal mid-
   * resolve). Aborting surfaces as `OnlineRoutingError({ kind: 'transport' })`.
   *
   * @throws {OnlineRoutingError} `timeout` / `transport` / `upstream` /
   *   `decode` — caller falls through to offline on any error.
   */
  fetchRoutes(
    fromNaptanId: string,
    toNaptanId: string,
    options?: OnlineRoutingOptions,
    signal?: AbortSignal,
  ): Promise<Route[]>
}

/** Default request timeout — matches iOS `OnlineRoutingClient.init(timeout:)`. */
export const ONLINE_ROUTING_TIMEOUT_MS = 3_000

/**
 * Maximum tolerated response body size (bytes). A legitimate routes
 * payload is well under 100 KB; 1 MB is a 10× safety margin that still
 * stops a compromised or misconfigured upstream from blowing up the tab
 * via a giant JSON. Decoder fails with `OnlineRoutingError('decode')`
 * once accumulated bytes cross this threshold, and the adapter then
 * falls back to offline routing.
 */
export const ONLINE_ROUTING_MAX_BODY_BYTES = 1_048_576

/**
 * Build a client over the supplied base URL. Pass `https://london-tube.com`
 * in production, `http://localhost:8002` for local dev.
 *
 * The optional `fetchImpl` seam lets tests inject a stub. Defaults to the
 * global `fetch`.
 */
export function buildOnlineRoutingClient(
  baseURL: string,
  opts: {
    readonly timeoutMs?: number
    readonly fetchImpl?: typeof fetch
    readonly maxBodyBytes?: number
  } = {},
): OnlineRoutingClient {
  const timeoutMs = opts.timeoutMs ?? ONLINE_ROUTING_TIMEOUT_MS
  const fetchImpl: typeof fetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis)
  const maxBodyBytes = opts.maxBodyBytes ?? ONLINE_ROUTING_MAX_BODY_BYTES
  // Reject any non-http(s) base URL — forward-defence for the day a
  // runtime override path lands (e.g. `?routingBase=...`). `new URL`
  // also catches malformed inputs (`baseURL = 'javascript:...'` throws
  // because `javascript:` URLs don't accept a base path).
  const parsed = new URL(baseURL)
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(`buildOnlineRoutingClient: baseURL must be http(s); got ${parsed.protocol}`)
  }
  const endpoint = `${baseURL.replace(/\/$/, '')}/api/v2/routes`

  return {
    async fetchRoutes(fromNaptanId, toNaptanId, options, externalSignal) {
      const body = _encodeBody(fromNaptanId, toNaptanId, options)
      const ctrl = new AbortController()
      const timer = setTimeout(() => {
        ctrl.abort()
      }, timeoutMs)
      // Bridge the caller-supplied signal into our internal controller
      // so the fetch aborts on either supersede / dismissal (external)
      // OR timeout (internal). We distinguish the two reasons via the
      // external signal's `aborted` flag post-throw to map external
      // aborts to `transport` (caller-driven cancellation) and timeouts
      // to `timeout`.
      const onExternalAbort = (): void => {
        ctrl.abort()
      }
      if (externalSignal !== undefined) {
        if (externalSignal.aborted) {
          clearTimeout(timer)
          throw new OnlineRoutingError({
            kind: 'transport',
            cause: new Error('aborted by caller'),
          })
        }
        externalSignal.addEventListener('abort', onExternalAbort, { once: true })
      }
      let resp: Response
      try {
        resp = await fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
          signal: ctrl.signal,
          // Routing endpoint is unauthenticated; sending cookies bloats
          // upstream logs and widens the CSRF / cookie-leakage surface
          // for nothing.
          credentials: 'omit',
        })
      } catch (cause) {
        clearTimeout(timer)
        externalSignal?.removeEventListener('abort', onExternalAbort)
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          if (externalSignal?.aborted === true) {
            throw new OnlineRoutingError({ kind: 'transport', cause })
          }
          throw new OnlineRoutingError({ kind: 'timeout' })
        }
        throw new OnlineRoutingError({ kind: 'transport', cause })
      } finally {
        clearTimeout(timer)
        externalSignal?.removeEventListener('abort', onExternalAbort)
      }

      // Early rejection on declared body size. The upstream is supposed
      // to set Content-Length on JSON responses; treat the header as
      // load-bearing when present.
      const contentLength = resp.headers.get('content-length')
      if (contentLength !== null) {
        const declared = Number.parseInt(contentLength, 10)
        if (Number.isFinite(declared) && declared > maxBodyBytes) {
          throw new OnlineRoutingError({
            kind: 'decode',
            cause: new Error(`response too large (Content-Length=${declared} > ${maxBodyBytes})`),
          })
        }
      }

      if (!resp.ok) {
        let truncated: string | undefined
        try {
          const txt = await _readBodyCapped(resp, maxBodyBytes)
          truncated = txt.slice(0, 200)
        } catch {
          // Body unreadable — best-effort; status is the load-bearing signal.
        }
        throw new OnlineRoutingError({ kind: 'upstream', status: resp.status, body: truncated })
      }

      let payload: unknown
      try {
        const text = await _readBodyCapped(resp, maxBodyBytes)
        payload = JSON.parse(text)
      } catch (cause) {
        throw new OnlineRoutingError({ kind: 'decode', cause })
      }

      const decoded = _decodeResponse(_unwrapEnvelope(payload))
      return _dedupBySignature(decoded.routes.map(_decodeRoute).map(recomputeID))
    },
  }
}

/**
 * Streamed body reader with a hard byte cap. Aborts the stream the
 * moment the accumulated chunks cross `maxBytes` so a hostile upstream
 * can't blow up the tab with a giant JSON. Returns the body decoded as
 * UTF-8 — `JSON.parse` runs on the result, mirroring the previous
 * `resp.json()` path but with a size guard.
 *
 * Falls back to `resp.text()` when streaming is unavailable (e.g. test
 * shims passing plain Response objects). The fallback still enforces
 * the cap post-hoc; the only downside is that an attacker could fully
 * load the body into memory before we reject. Acceptable when the
 * primary path uses the streaming reader.
 */
async function _readBodyCapped(resp: Response, maxBytes: number): Promise<string> {
  const body = resp.body
  if (body === null || typeof body.getReader !== 'function') {
    const txt = await resp.text()
    if (txt.length > maxBytes) {
      throw new Error(`response too large (${txt.length} bytes > ${maxBytes})`)
    }
    return txt
  }
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let total = 0
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      if (value === undefined) continue
      total += value.byteLength
      if (total > maxBytes) {
        await reader.cancel()
        throw new Error(`response too large (>${maxBytes} bytes)`)
      }
      chunks.push(value)
    }
  } finally {
    reader.releaseLock?.()
  }
  const merged = new Uint8Array(total)
  let off = 0
  for (const c of chunks) {
    merged.set(c, off)
    off += c.byteLength
  }
  return new TextDecoder('utf-8').decode(merged)
}

// MARK: — Request encoder

function _encodeBody(
  fromNaptanId: string,
  toNaptanId: string,
  options?: OnlineRoutingOptions,
): string {
  const opts: Record<string, string> = {}
  if (options?.departAt !== undefined && options.departAt.length > 0) {
    opts.depart_at = options.departAt
  }
  if (options?.arriveAt !== undefined && options.arriveAt.length > 0) {
    opts.arrive_at = options.arriveAt
  }
  if (
    options?.accessibilityPreference !== undefined &&
    options.accessibilityPreference.length > 0
  ) {
    opts.accessibility_pref = options.accessibilityPreference
  }
  return JSON.stringify({ from: fromNaptanId, to: toNaptanId, options: opts })
}

// MARK: — Wire-format decoders

interface _WireResponse {
  readonly routes: readonly unknown[]
  readonly source?: string
  readonly staleness?: string
}

function _decodeResponse(raw: unknown): _WireResponse {
  if (raw === null || typeof raw !== 'object') {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error('response is not an object'),
    })
  }
  const obj = raw as Record<string, unknown>
  const routes = obj.routes
  if (!Array.isArray(routes)) {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error('response.routes is not an array'),
    })
  }
  return {
    routes,
    source: typeof obj.source === 'string' ? obj.source : undefined,
    staleness: typeof obj.staleness === 'string' ? obj.staleness : undefined,
  }
}

/** The city server wraps every body in `{ "data": …, "success": … }`. */
function _unwrapEnvelope(raw: unknown): unknown {
  if (raw === null || typeof raw !== 'object') return raw
  return (raw as Record<string, unknown>).data
}

function _decodeRoute(raw: unknown): Route {
  if (raw === null || typeof raw !== 'object') {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error('route is not an object'),
    })
  }
  const obj = raw as Record<string, unknown>
  const legsRaw = obj.legs
  if (!Array.isArray(legsRaw)) {
    throw new OnlineRoutingError({ kind: 'decode', cause: new Error('legs is not an array') })
  }
  return {
    id: typeof obj.id === 'string' ? obj.id : '',
    total_time_s: _decodeInt(obj.total_time_s, 'total_time_s'),
    transfer_count: _decodeInt(obj.transfer_count, 'transfer_count'),
    walking_time_s: _decodeInt(obj.walking_time_s, 'walking_time_s'),
    legs: legsRaw.map(_decodeLeg),
  }
}

function _decodeLeg(raw: unknown): Route['legs'][number] {
  if (raw === null || typeof raw !== 'object') {
    throw new OnlineRoutingError({ kind: 'decode', cause: new Error('leg is not an object') })
  }
  const obj = raw as Record<string, unknown>
  const type = obj.type
  if (type !== 'ride' && type !== 'transfer' && type !== 'walk') {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error(`leg.type unrecognised: ${String(type)}`),
    })
  }
  const stopsRaw = obj.stops
  const stops: Stop[] = _collapseTrailingArrivalDup(
    Array.isArray(stopsRaw) ? stopsRaw.map(_decodeStop) : [],
  )
  // `convenient_cars` (backend wire, transfer legs only, omitempty): keep
  // only the string tokens; absent / malformed → undefined.
  const convenientCarsRaw = obj.convenient_cars
  const convenientCars = Array.isArray(convenientCarsRaw)
    ? convenientCarsRaw.filter((c): c is string => typeof c === 'string')
    : undefined
  return {
    type: type as LegType,
    line_id: typeof obj.line_id === 'string' ? obj.line_id : '',
    stops,
    hint_carriage: typeof obj.hint_carriage === 'string' ? obj.hint_carriage : undefined,
    transition_id: typeof obj.transition_id === 'string' ? obj.transition_id : '',
    from_station_id: typeof obj.from_station_id === 'string' ? obj.from_station_id : '',
    to_station_id: typeof obj.to_station_id === 'string' ? obj.to_station_id : '',
    duration_s: _decodeInt(obj.duration_s, 'duration_s'),
    convenient_cars: convenientCars,
  }
}

/**
 * Defensive trailing arrival-duplicate collapse (mirrors server fix
 * `ed452836`, cross-platform with iOS/Android). Some server builds appended
 * an ArrivalPoint over TFL stopPoints that already ended at the arrival,
 * duplicating the last two stops — over-counting the "N stations" chip and
 * listing the destination twice. When the final two stops share the same
 * station (naptan-preferred: `naptanId ?? stationId`), drop the LAST stop.
 *
 * TRAILING only: interior stops and the departure-side handover (`stops[0]`)
 * are never touched. Idempotent — a no-op once the server ships de-dupped
 * stops, so it's safe to keep as a permanent client-side guard against an
 * older / not-yet-deployed server or a future regression.
 */
function _collapseTrailingArrivalDup(stops: Stop[]): Stop[] {
  if (stops.length < 2) return stops
  const last = stops[stops.length - 1]
  const prev = stops[stops.length - 2]
  const lastKey = last.naptanId ?? last.stationId
  const prevKey = prev.naptanId ?? prev.stationId
  return lastKey === prevKey ? stops.slice(0, -1) : stops
}

function _decodeStop(raw: unknown): Stop {
  if (raw === null || typeof raw !== 'object') {
    throw new OnlineRoutingError({ kind: 'decode', cause: new Error('stop is not an object') })
  }
  const obj = raw as Record<string, unknown>
  const stationId = obj.stationId
  if (typeof stationId !== 'string' || stationId.length === 0) {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error('stop.stationId missing'),
    })
  }
  return {
    stationId,
    arrival_offset_s: _decodeInt(obj.arrival_offset_s, 'arrival_offset_s'),
    naptanId: typeof obj.naptanId === 'string' ? obj.naptanId : undefined,
    passthrough: typeof obj.passthrough === 'boolean' ? obj.passthrough : false,
  }
}

function _decodeInt(raw: unknown, field: string): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    throw new OnlineRoutingError({
      kind: 'decode',
      cause: new Error(`${field} is not a finite number: ${String(raw)}`),
    })
  }
  return Math.trunc(raw)
}

// MARK: — Defence-in-depth filter (port of iOS `_dedupBySignature`)

/**
 * Drop signature-equivalent routes preserving the first occurrence. Mirrors
 * iOS `_dedupBySignature` (`OnlineRoutingClient.swift:249–265`). The
 * backend mapper also dedups (`MapJourneysWithAnchors`) but TFL has been
 * caught shipping clock-only variants that survive server-side dedup; this
 * keeps the client list clean regardless.
 */
/** @internal — exported for direct unit-test coverage. Mirror of iOS
 * `OnlineRoutingClientTests.test_fetchRoutes_dedupCollapsesDuplicateSignatureRoutes`. */
export const __internal_dedupBySignature = _dedupBySignature
function _dedupBySignature(routes: readonly Route[]): Route[] {
  const seen = new Set<string>()
  const out: Route[] = []
  for (const r of routes) {
    const key = signatureKey(r)
    // Walking-only routes have NO ride legs → `signatureKey` is `''`.
    // Collapsing all empty-sig routes to one would silently merge
    // distinct pedestrian variants (e.g. TFL emitting both "walk
    // direct" and "walk via X" for the same OD pair). Mirrors iOS
    // `_dedupBySignature` (`OnlineRoutingClient.swift:256–259`).
    if (key.length === 0) {
      out.push(r)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push(r)
  }
  return out
}
